// Mock Medications Data
// Sample medication data for demonstration purposes

export interface MockMedication {
  id: string;
  name: string;
  genericName: string;
  dosage: string;
  frequency: string;
  instructions: string;
  category: 'diabetes' | 'hypertension' | 'cholesterol' | 'pain' | 'mental-health' | 'other';
  startDate: string;
  endDate?: string;
  prescribedBy: string;
  pharmacy: string;
  refillsRemaining: number;
  sideEffects: string[];
  interactions: string[];
  isActive: boolean;
  notes?: string;
}

export const mockMedications: MockMedication[] = [
  {
    id: 'med-001',
    name: 'Metformin',
    genericName: 'Metformin Hydrochloride',
    dosage: '500mg',
    frequency: 'Twice daily with meals',
    instructions: 'Take with food to reduce stomach upset. Monitor blood sugar regularly.',
    category: 'diabetes',
    startDate: '2024-06-15',
    prescribedBy: 'Dr. Sarah Chen',
    pharmacy: 'CVS Pharmacy',
    refillsRemaining: 2,
    sideEffects: ['Nausea', 'Diarrhea', 'Stomach upset'],
    interactions: ['Alcohol', 'Certain antibiotics'],
    isActive: true,
    notes: 'Started for type 2 diabetes management'
  },
  {
    id: 'med-002',
    name: 'Lisinopril',
    genericName: 'Lisinopril',
    dosage: '10mg',
    frequency: 'Once daily in the morning',
    instructions: 'Take at the same time each day. Monitor blood pressure regularly.',
    category: 'hypertension',
    startDate: '2024-05-20',
    prescribedBy: 'Dr. Michael Rodriguez',
    pharmacy: 'Walgreens',
    refillsRemaining: 1,
    sideEffects: ['Dry cough', 'Dizziness', 'Fatigue'],
    interactions: ['NSAIDs', 'Potassium supplements'],
    isActive: true,
    notes: 'Blood pressure medication'
  },
  {
    id: 'med-003',
    name: 'Atorvastatin',
    genericName: 'Atorvastatin Calcium',
    dosage: '20mg',
    frequency: 'Once daily in the evening',
    instructions: 'Take in the evening for best effect. Avoid grapefruit juice.',
    category: 'cholesterol',
    startDate: '2024-04-10',
    prescribedBy: 'Dr. Emily Watson',
    pharmacy: 'Rite Aid',
    refillsRemaining: 3,
    sideEffects: ['Muscle pain', 'Liver enzyme changes', 'Digestive issues'],
    interactions: ['Grapefruit juice', 'Certain antibiotics'],
    isActive: true,
    notes: 'Cholesterol-lowering medication'
  },
  {
    id: 'med-004',
    name: 'Ibuprofen',
    genericName: 'Ibuprofen',
    dosage: '400mg',
    frequency: 'As needed for pain',
    instructions: 'Take with food. Do not exceed 3200mg per day.',
    category: 'pain',
    startDate: '2024-01-15',
    endDate: '2024-12-31',
    prescribedBy: 'Dr. James Wilson',
    pharmacy: 'Local Pharmacy',
    refillsRemaining: 0,
    sideEffects: ['Stomach irritation', 'Increased bleeding risk'],
    interactions: ['Blood thinners', 'Other NSAIDs'],
    isActive: true,
    notes: 'Over-the-counter pain relief'
  },
  {
    id: 'med-005',
    name: 'Sertraline',
    genericName: 'Sertraline Hydrochloride',
    dosage: '50mg',
    frequency: 'Once daily in the morning',
    instructions: 'Take consistently at the same time. May take 2-4 weeks for full effect.',
    category: 'mental-health',
    startDate: '2024-03-01',
    prescribedBy: 'Dr. Lisa Thompson',
    pharmacy: 'CVS Pharmacy',
    refillsRemaining: 2,
    sideEffects: ['Nausea', 'Insomnia', 'Sexual dysfunction'],
    interactions: ['MAOIs', 'Blood thinners'],
    isActive: true,
    notes: 'Antidepressant for anxiety and depression'
  },
  {
    id: 'med-006',
    name: 'Omeprazole',
    genericName: 'Omeprazole',
    dosage: '20mg',
    frequency: 'Once daily before breakfast',
    instructions: 'Take on empty stomach 30 minutes before eating.',
    category: 'other',
    startDate: '2024-02-15',
    prescribedBy: 'Dr. Robert Kim',
    pharmacy: 'Walgreens',
    refillsRemaining: 1,
    sideEffects: ['Headache', 'Diarrhea', 'Vitamin B12 deficiency'],
    interactions: ['Iron supplements', 'Certain antibiotics'],
    isActive: true,
    notes: 'Acid reflux medication'
  },
  {
    id: 'med-007',
    name: 'Aspirin',
    genericName: 'Acetylsalicylic Acid',
    dosage: '81mg',
    frequency: 'Once daily',
    instructions: 'Take with food to prevent stomach upset.',
    category: 'other',
    startDate: '2023-12-01',
    prescribedBy: 'Dr. Sarah Chen',
    pharmacy: 'Local Pharmacy',
    refillsRemaining: 0,
    sideEffects: ['Stomach irritation', 'Bleeding risk'],
    interactions: ['Blood thinners', 'Other NSAIDs'],
    isActive: true,
    notes: 'Low-dose aspirin for heart health'
  },
  {
    id: 'med-008',
    name: 'Vitamin D3',
    genericName: 'Cholecalciferol',
    dosage: '2000 IU',
    frequency: 'Once daily',
    instructions: 'Take with a meal containing fat for better absorption.',
    category: 'other',
    startDate: '2024-01-01',
    prescribedBy: 'Dr. Emily Watson',
    pharmacy: 'CVS Pharmacy',
    refillsRemaining: 0,
    sideEffects: ['Rare at this dose'],
    interactions: ['Calcium supplements'],
    isActive: true,
    notes: 'Vitamin D supplement for bone health'
  }
];

// Medication categories for filtering
export const medicationCategories = [
  { id: 'all', name: 'All Medications', count: mockMedications.length },
  { id: 'diabetes', name: 'Diabetes', count: mockMedications.filter(m => m.category === 'diabetes').length },
  { id: 'hypertension', name: 'Hypertension', count: mockMedications.filter(m => m.category === 'hypertension').length },
  { id: 'cholesterol', name: 'Cholesterol', count: mockMedications.filter(m => m.category === 'cholesterol').length },
  { id: 'pain', name: 'Pain Management', count: mockMedications.filter(m => m.category === 'pain').length },
  { id: 'mental-health', name: 'Mental Health', count: mockMedications.filter(m => m.category === 'mental-health').length },
  { id: 'other', name: 'Other', count: mockMedications.filter(m => m.category === 'other').length }
];

// Sample medication interactions
export const medicationInteractions = [
  {
    medication1: 'Metformin',
    medication2: 'Alcohol',
    severity: 'moderate',
    description: 'May increase risk of lactic acidosis'
  },
  {
    medication1: 'Lisinopril',
    medication2: 'Ibuprofen',
    severity: 'high',
    description: 'May reduce blood pressure medication effectiveness'
  },
  {
    medication1: 'Atorvastatin',
    medication2: 'Grapefruit Juice',
    severity: 'high',
    description: 'May increase statin levels in blood'
  }
];

// Sample medication reminders
export const medicationReminders = [
  {
    id: 'reminder-001',
    medicationId: 'med-001',
    time: '08:00',
    days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
    isActive: true
  },
  {
    id: 'reminder-002',
    medicationId: 'med-001',
    time: '18:00',
    days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
    isActive: true
  },
  {
    id: 'reminder-003',
    medicationId: 'med-002',
    time: '08:00',
    days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
    isActive: true
  }
]; 